import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import Header from "./Header/Header";
import Sidebar from "./Sidebar";
import { useSelector } from "react-redux";
import { selectOrganization } from "../store/slices/authUserSlice";
import { getAuthUser } from "../services/authService";
import { SidebarContext } from "../contexts/SidebarContext";

interface LayoutProps {
  children: React.ReactNode;
}

const BANNER_EXPIRY = new Date("2026-06-18").getTime(); // 4 weeks from launch

const getBannerKey = () => {
  const user = getAuthUser();
  return `interview_banner_dismissed_${user?.userDetails?.email || "anon"}`;
};

const getSunsetBannerKey = () => {
  const user = getAuthUser();
  return `sunset_banner_dismissed_${user?.userDetails?.email || "anon"}`;
};

const PageWithLayout: React.FC<LayoutProps> = ({children}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const urlPath = location.pathname;
  const showSidebar = urlPath !== "/" && urlPath !== "/login" && urlPath !== "/signup" && !urlPath.startsWith("/templates") && !urlPath.startsWith("/instant");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth <= 768);
  const [bannerDismissed, setBannerDismissed] = useState(() => localStorage.getItem(getBannerKey()) === "true");
  const [sunsetBannerDismissed, setSunsetBannerDismissed] = useState(() => localStorage.getItem(getSunsetBannerKey()) === "true");
  const organization = useSelector(selectOrganization);
  const hasInterviews = !!organization?.feature_flags?.content_interviews;

  const showBanner = showSidebar && hasInterviews && !bannerDismissed && Date.now() < BANNER_EXPIRY && !urlPath.startsWith("/self-interviews") && !urlPath.startsWith("/content-interviews");
  const sunsetRecent = (() => {
    const lastSunset = organization?.last_sunset_at;
    if (!lastSunset) return false;
    const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
    return Date.now() - new Date(lastSunset).getTime() < thirtyDaysMs;
  })();
  const showSunsetBanner = showSidebar && sunsetRecent && !sunsetBannerDismissed;

  const dismissBanner = () => {
    setBannerDismissed(true);
    localStorage.setItem(getBannerKey(), "true");
  };

  const dismissSunsetBanner = () => {
    setSunsetBannerDismissed(true);
    localStorage.setItem(getSunsetBannerKey(), "true");
  };

  return (
    <SidebarContext.Provider value={{ collapsed: sidebarCollapsed, setCollapsed: setSidebarCollapsed }}>
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
      }}>
      <Header />
      {showBanner && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: "12px", padding: "10px 20px",
          backgroundColor: "#EEF2FF", borderBottom: "1px solid #C7D2FE",
        }}>
          <span style={{ fontSize: "14px", color: "#374151" }}>
            <strong>New:</strong> Answer AI-generated interview questions and we'll turn your responses into newsletter content.
          </span>
          <button
            onClick={() => navigate("/content-interviews")}
            style={{
              padding: "4px 14px", borderRadius: "6px",
              backgroundColor: "#6366F1", color: "#FFFFFF", border: "none",
              fontSize: "13px", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            Try it
          </button>
          <button
            onClick={dismissBanner}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "4px", display: "flex", color: "#9CA3AF",
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}
      {showSunsetBanner && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: "12px", padding: "10px 20px",
          backgroundColor: "#FFF7ED", borderBottom: "1px solid #FED7AA",
        }}>
          <span style={{ fontSize: "14px", color: "#374151" }}>
            <strong>Heads up:</strong> We've temporarily trimmed your list to improve deliverability & engagement.
          </span>
          <button
            onClick={dismissSunsetBanner}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "4px", display: "flex", color: "#9CA3AF",
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}
      <div
        style={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}>
        { showSidebar && (
          <div style={{ position: "relative", display: "flex", flexShrink: 0, height: "100%" }}>
            <div style={{
              width: sidebarCollapsed ? "0px" : "175px",
              overflow: "hidden",
              transition: "width 0.2s ease",
              height: "100%",
            }}>
              <Sidebar />
            </div>
            <button
              onClick={() => setSidebarCollapsed((prev) => !prev)}
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              style={{
                position: "absolute",
                top: "8px",
                right: "-14px",
                zIndex: 10,
                width: "28px",
                height: "28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: "50%",
                cursor: "pointer",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                padding: 0,
              }}
            >
              {sidebarCollapsed
                ? <PanelLeftOpen size={14} color="#666" />
                : <PanelLeftClose size={14} color="#666" />
              }
            </button>
          </div>
        )}
        <main
          style={{
            flex: 1,
            padding: "0px 30px",
            marginLeft: 0,
            backgroundColor: "#f9f9f9",
            overflowY: "auto",
          }}>
          {children}
        </main>
      </div>
    </div>
    </SidebarContext.Provider>
  );
};

export default PageWithLayout;
